import { treatmentFlagValues, TreatmentName } from 'constants/treatments'
import RudderStackContext from 'context/common/RudderStackContext'
import useIsRolloutPlanSettled from 'hooks/treatments/useIsRolloutPlanSettled'
import { isRolloutPlanSettled } from 'lib/splitRolloutPlan'
import { useContext, useEffect, useMemo, useState } from 'react'
import {
  getTreatment,
  isSplitAnalyticsConsentGranted,
  isSplitReady,
  isSplitSettled,
} from 'utils/treatment'

/**
 * This hook returns the valid value for a specified treatment. As it takes some time
 * while initializing split client, so until the split client is ready we keep calling
 * the getTreatment function after every 200ms time interval until we get valid value
 * for a particular treatment instead of 'control'.
 *
 * @param treatmentName
 * Required parameter for this hook, pass in the split treatment name.
 *
 * @param allowToFetchTreatment
 * A optional parameter to control when the getTreatment function should be called.
 *
 * @returns returns the treatment value for the specified treatment
 */
const useGetTreatment = (
  name: TreatmentName,
  allowToFetchTreatment = true,
  attributes?: SplitIO.Attributes
): string => {
  const attributesDependencyKey = useMemo(
    () => JSON.stringify(attributes ?? {}),
    [attributes]
  )
  const rolloutPlanSettled = useIsRolloutPlanSettled()
  // The module-level consent flag in utils/treatment is not reactive; the
  // context value re-renders consumers, so it re-arms the CONTROL poll when
  // consent is granted after a consent-denied CONTROL was committed.
  const { analyticsConsentGranted } = useContext(RudderStackContext)
  const [treatmentValue, setTreatmentValue] = useState<string>(() =>
    allowToFetchTreatment
      ? getTreatment(name, attributes)
      : treatmentFlagValues.CONTROL
  )
  // SDK_READY_TIMED_OUT leaves the value at CONTROL, so setTreatmentValue alone
  // would not re-render consumers that read isSplitSettled()/isSplitTimedOut()
  // live. Force a tick so they can release on a terminal CONTROL.
  const [, forceRerender] = useState(0)

  const setTreatmentFromSplit = () => {
    const updatedTreatmentValue = getTreatment(name, attributes)
    setTreatmentValue(updatedTreatmentValue)
    forceRerender((tick) => tick + 1)
  }

  /* 
    TODO: Need to find a better way to get the valid value for a treatment instead of
    keep on calling getTreatment function until the Split client is initialized. Check
    with Split.io react SDK (https://github.com/splitio/react-client). This can be used
    later on so that we don't have to handle the split IO implementation ourselves.
  */

  /*
    This is called only for the first time when split is getting ready.
  */
  useEffect(() => {
    let treatmentIntervalHandle

    if (
      allowToFetchTreatment &&
      treatmentValue === treatmentFlagValues.CONTROL
    ) {
      if (
        isSplitSettled() ||
        (rolloutPlanSettled && !isSplitAnalyticsConsentGranted())
      ) {
        setTreatmentFromSplit()
      } else {
        treatmentIntervalHandle = setInterval(() => {
          if (
            isSplitSettled() ||
            (isRolloutPlanSettled() && !isSplitAnalyticsConsentGranted())
          ) {
            setTreatmentFromSplit()
            clearInterval(treatmentIntervalHandle)
          }
        }, 100)
      }
    }

    return () => clearInterval(treatmentIntervalHandle)
  }, [allowToFetchTreatment, rolloutPlanSettled, analyticsConsentGranted])

  /*
    This useEffect is when any of the attributes change, to invoke the getTreatment function again
  */
  useEffect(() => {
    if (
      allowToFetchTreatment &&
      isSplitReady() &&
      treatmentValue !== treatmentFlagValues.CONTROL
    ) {
      setTreatmentFromSplit()
    }
  }, [attributesDependencyKey])

  // Consent-denied + settled: return the live released value in the same render
  // that settlement flips, so consumers do not act on stale CONTROL for up to
  // one poll interval (100ms).
  if (
    allowToFetchTreatment &&
    treatmentValue === treatmentFlagValues.CONTROL &&
    rolloutPlanSettled &&
    !isSplitAnalyticsConsentGranted()
  ) {
    return getTreatment(name, attributes)
  }

  return treatmentValue
}

export default useGetTreatment
